import { inject, Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, of, switchMap, tap, timeout } from 'rxjs';
import { DateTime } from 'luxon';
import * as Sentry from '@sentry/angular';

/**
 * A public time endpoint and where the instant sits in its payload.
 */
type TTimeSource = {
  readonly url: string;
  /** Field carrying an ISO instant. Preferred — it has sub-second precision. */
  readonly isoField: string;
  /** Field carrying epoch **seconds**, read when the ISO one is missing. */
  readonly epochSecondsField: string;
};

/**
 * Tried in order; the first one that answers with a plausible instant wins.
 * Both send CORS headers and need no API key.
 */
const TIME_SOURCES: readonly TTimeSource[] = [
  {
    url: 'https://gettimeapi.dev/v1/time?timezone=UTC',
    isoField: 'iso8601',
    epochSecondsField: 'timestamp',
  },
  {
    url: 'https://time.now/developer/api/timezone/UTC',
    isoField: 'utc_datetime',
    epochSecondsField: 'unixtime',
  },
];

/** Per-source ceiling, so a hanging host cannot stall the chain. */
const REQUEST_TIMEOUT_MS = 4_000;

/**
 * How far the wall clock may drift from the monotonic counter before the anchor
 * is treated as untrustworthy. Both readings advance together on a healthy
 * machine, so a couple of seconds is generous for timer coalescing in a
 * background tab.
 */
const DRIFT_TOLERANCE_MS = 2_000;

/** Floor between two fetches, so a flapping tab cannot hammer the sources. */
const MIN_RESYNC_INTERVAL_MS = 60_000;

/**
 * Espera mínima antes de tentar outra vez, enquanto NADA está ancorado, e o teto
 * do recuo exponencial que a multiplica a cada falha seguida.
 *
 * A falha de arranque não pode ser definitiva. Antes disto o `sync()` corria uma
 * vez no bootstrap e depois só no `visibilitychange` — e um PC de balcão que
 * abre o browser antes de a rede associar, ou atrás de um portal cativo, ficava
 * o resto da sessão com o relógio da máquina, sem nunca mais tentar: o
 * separador em modo quiosque nunca perde visibilidade. Era esse o estado do
 * cliente do Sentry THE-NEW-R6, a enviar datas três dias atrasadas.
 *
 * O recuo existe porque a causa mais provável de uma falha é a rede do clube
 * bloquear os domínios — e nesse caso a tentativa nunca vai resultar. Insistir
 * de minuto a minuto até ao fim do dia seriam milhares de pedidos falhados por
 * estação, para nada.
 */
const RETRY_BASE_MS = 60_000;
const RETRY_MAX_MS = 15 * 60_000;

/**
 * Ritmo a que o relógio é revalidado depois de ancorado.
 *
 * A âncora deriva do contador monotónico, que não erra por si; isto existe para
 * o que ele não consegue medir — suspensões, e o relógio mexido a meio da
 * sessão — em estações que ficam dias com o mesmo separador aberto e onde o
 * `visibilitychange` nunca dispara.
 */
const PERIODIC_RESYNC_MS = 30 * 60_000;

/**
 * Sanity window for whatever a third party returns. A source that answers with
 * garbage (epoch 0, a year 1970 date, a truncated number) would poison every
 * date guard in the app, so anything outside this range is discarded and the
 * next source is tried.
 */
const PLAUSIBLE_FROM_MS = DateTime.utc(2020, 1, 1).toMillis();
const PLAUSIBLE_TO_MS = DateTime.utc(2100, 1, 1).toMillis();

/**
 * Época do "agora" de último recurso, usado quando NADA é utilizável: sem âncora
 * e com um relógio de máquina que o `Intl` recusa (`NaN`, `±Infinity`, fora do
 * intervalo do `Date`). O {@link lastResortMillis} fá-la avançar.
 *
 * Tem de ser um valor formatável, e não há alternativa a devolver algum: o
 * `SERVER_CLOCK_INITIALIZER` liga isto a `Settings.now`, portanto um `NaN` que
 * escape daqui torna inválido TODO o `DateTime.now()` da aplicação — `toISO()`
 * passa a `null`, `toLocaleString()` a `"Invalid DateTime"`, e o primeiro `Intl`
 * que receber o valor lança `RangeError: Invalid time value`. Foi exatamente
 * isso o Sentry THE-NEW-130, que derrubou a máscara de todos os inputs de data.
 *
 * É deliberadamente o piso da janela de plausibilidade — 2020-01-01, um valor
 * ÓBVIA e visivelmente errado — e não um palpite credível. Um "agora" inventado
 * que pareça bom passaria pelas validações e gravaria datas erradas em silêncio;
 * uma data destas é rejeitada à vista, pelo utilizador e pelo backend.
 */
const LAST_RESORT_MS = PLAUSIBLE_FROM_MS;

/** Maior instante que o `Intl` aceita — o limite do tipo `Date` do ECMAScript. */
const MAX_TIME_VALUE_MS = 8.64e15;

/**
 * Se o `Intl` consegue formatar este instante. `format` e `formatToParts`
 * **lançam** `RangeError: Invalid time value` para `NaN`, `±Infinity` e para
 * qualquer finito acima de {@link MAX_TIME_VALUE_MS}.
 *
 * Gémeo do `isFormattableInstant` de `@shared/utils`, e duplicado de propósito:
 * importar esse barril daqui fecha um ciclo entre libs
 * (`application-data-access` → `shared-utils` → … → `settings-data-access` →
 * `application-data-access`), que o `@nx/enforce-module-boundaries` recusa. São
 * três linhas sem estado; o ciclo custava bem mais.
 */
function isFormattableInstant(ms: number): boolean {
  return Number.isFinite(ms) && Math.abs(ms) <= MAX_TIME_VALUE_MS;
}

/**
 * Source of "now" that does not trust the machine clock.
 *
 * Every date guard in the app is ultimately `DateTime.now()`, i.e. the operating
 * system clock. When that clock is wrong the guards are wrong with it: a station
 * running a day behind highlights the wrong cell as "today" in the datepicker,
 * `minDateValidator` accepts it, and only the backend — the one participant with
 * a correct clock — rejects the request (see the `Subscription/Close` "data de
 * fechamento não pode ser anterior a hoje" reports).
 *
 * So the time is fetched once, at startup, from a public time API. NTP itself
 * (UDP/123, e.g. the `ntp.br` pool) is unreachable from a browser — there is no
 * UDP socket in JavaScript — so HTTP is the only transport available.
 *
 * ## Keeping it current without further requests
 *
 * The fetched instant is stored as an **anchor** together with a reading of
 * `performance.now()`, and every later "now" is the anchor plus how much that
 * counter advanced. `performance.now()` is monotonic and independent of the
 * system clock, which is what makes this hold: the user can change the machine
 * clock mid-session — precisely the scenario this guards against — and the
 * derived time does not budge.
 *
 * On some platforms that counter does not advance while the machine is
 * suspended, so a station left asleep comes back behind. The anchor also records
 * `Date.now()`, and the two readings must advance together — when they disagree,
 * either the machine slept or the clock was changed. {@link nowMillis} deteta
 * essa divergência e passa a derivar do relógio de parede, para não devolver um
 * instante velho enquanto ninguém pedir uma re-sincronização; o
 * {@link resyncIfDrifted} resolve a causa, voltando a buscar a hora.
 *
 * ## Failure is not fatal
 *
 * If every source fails (offline, DNS blocked by the venue's firewall), nothing
 * is anchored and {@link nowMillis} is exactly `Date.now()` — the behaviour the
 * app had before this existed. {@link isSynced} tells the two states apart.
 *
 * ## Mas o relógio da máquina também pode não servir de todo
 *
 * Cair para o `Date.now()` só é uma degradação aceitável enquanto ele devolver
 * um instante que o resto da plataforma consiga usar. Um relógio fora do
 * intervalo representável do `Date` devolve `NaN`, e como isto está ligado a
 * `Settings.now`, esse `NaN` torna inválido TODO o `DateTime.now()` da app — e
 * o primeiro `Intl` a recebê-lo lança `RangeError: Invalid time value` (Sentry
 * THE-NEW-130). O {@link nowMillis} garante por isso uma invariante de saída:
 * nunca emite um instante que o `Intl` recuse. {@link isClockUsable} diz quando
 * essa rede de segurança teve de ser usada.
 */
@Injectable({ providedIn: 'root' })
export class ServerClockService {
  /**
   * Deliberately built off `HttpBackend`: these are third-party hosts, and the
   * interceptor chain would attach the session token to them, run the `*Utc`
   * conversion over their payloads, and — worst of all — hand a 401 from the
   * time API to the token interceptor, which would answer it with a refresh.
   */
  private readonly http = new HttpClient(inject(HttpBackend));

  /** The fetched instant. `null` = never synced, running on the local clock. */
  private anchorServerMs: number | null = null;
  /** `performance.now()` as read at the same moment as the anchor. */
  private anchorTickMs = 0;
  /** `Date.now()` as read at the same moment as the anchor, to measure drift. */
  private anchorWallMs = 0;
  /** Monotonic stamp of the last fetch attempt, for the re-sync floor. */
  private lastAttemptTickMs = Number.NEGATIVE_INFINITY;
  /** Se a última derivação do {@link nowMillis} deu um instante utilizável. */
  private clockUsable = true;
  /** Trava do relatório ao Sentry: o {@link nowMillis} corre milhares de vezes. */
  private clockUnusableReported = false;
  /** Falhas seguidas do {@link sync}, para o recuo exponencial das tentativas. */
  private consecutiveFailures = 0;

  /**
   * Fetches the time once, walking {@link TIME_SOURCES} until one answers.
   * Resolves to whether the clock ended up anchored; never errors.
   */
  public sync(): Observable<boolean> {
    this.lastAttemptTickMs = tick();

    return TIME_SOURCES.reduce<Observable<boolean>>(
      (previous, source) =>
        previous.pipe(
          switchMap((anchored) => (anchored ? of(true) : this.readSource(source))),
        ),
      of(false),
    ).pipe(
      tap((anchored) => {
        this.consecutiveFailures = anchored ? 0 : this.consecutiveFailures + 1;
        this.reportClockState();
      }),
    );
  }

  /**
   * Epoch millis of "now": the anchored instant advanced by the monotonic
   * counter, or the machine clock while nothing is anchored.
   *
   * Arredondado porque `performance.now()` tem resolução abaixo do milissegundo:
   * sem isto o retorno é fracionário, e como o `SERVER_CLOCK_INITIALIZER` o liga
   * a `Settings.now`, todo o `DateTime.now().toMillis()` da aplicação passava a
   * sê-lo também — inclusive os valores que são gravados como timestamp
   * (`temporaryAuthExpiresAt` no `auth.store`, o carimbo do `chunk-load-error`
   * no `sessionStorage`). `Date.now()`, o outro ramo, é inteiro por definição.
   *
   * ## Quando o contador monotónico deixa de medir o tempo
   *
   * Em suspensão, `performance.now()` congela em várias plataformas enquanto o
   * mundo avança, e a partir daí `tick()` mede menos do que o tempo real: sem a
   * guarda abaixo, uma máquina que dormiu 8h com a app aberta devolvia um "now"
   * 8h no passado a TODA a aplicação — `getToday()` no dia anterior, validações
   * de check-in a aceitar registos velhos, `minDateValidator` a deixar passar
   * datas idas. O {@link resyncIfDrifted} corrige isso, mas só corre no
   * `visibilitychange`, que NÃO dispara num tablet de portaria que acorda com o
   * separador já visível — exatamente a estação onde isto dói mais.
   *
   * Por isso a divergência é detetada aqui e, enquanto durar, o instante é
   * derivado do relógio de PAREDE em vez do contador. Numa suspensão é o ramo
   * certo: o RTC continuou a contar, logo `Date.now() - anchorWallMs` é o tempo
   * real decorrido. E mantém-se o desvio medido na âncora, que é o que este
   * serviço existe para corrigir — cair para um `Date.now()` cru devolveria a
   * estação ao erro do seu próprio relógio (o caso do balcão um dia atrasado).
   *
   * A guarda é DIRECIONAL de propósito, e é o que a mantém compatível com a
   * promessa do serviço. O contador nunca corre mais depressa que o tempo real,
   * logo só o caso "parede à frente do contador" é assinatura de suspensão. O
   * contrário — parede atrás — é inequivocamente um relógio mexido, e aí manda o
   * contador, como sempre mandou.
   *
   * Fica um caso genuinamente ambíguo: o relógio adiantado a meio da sessão tem
   * a mesma assinatura de uma suspensão. Aí seguimos a parede, o que é a
   * escolha menos má — um erro de suspensão não tratado deixa a app horas atrás
   * em silêncio e para sempre se as fontes estiverem bloqueadas pela rede do
   * clube, enquanto seguir um relógio adiantado degrada, no máximo, para o
   * comportamento que a app tinha antes deste serviço existir. O
   * `resyncIfDrifted` desfaz a ambiguidade assim que puder, voltando a ancorar.
   */
  public nowMillis(): number {
    const derived = this.deriveNowMillis();
    if (isFormattableInstant(derived)) {
      if (!this.clockUsable) {
        // Recuperou — o relógio foi acertado, ou uma âncora nova tapou-o. Sem
        // isto a estação ficava marcada como doente para sempre, e a trava do
        // relatório abafava uma recaída posterior, verdadeira.
        this.clockUsable = true;
        this.clockUnusableReported = false;
      }
      return derived;
    }

    // Nem o relógio da máquina nem a derivação da âncora deram um instante que o
    // `Intl` aceite.
    this.reportUnusableClock(derived);
    return lastResortMillis();
  }

  /**
   * O cálculo do "agora", sem qualquer garantia sobre o resultado — o
   * {@link nowMillis} é que o valida antes de o deixar sair.
   *
   * Está separado porque cada uma das suas saídas depende do `Date.now()`, o
   * relógio de que este serviço existe para desconfiar, e um relógio partido não
   * devolve só uma hora errada: devolve `NaN` quando está fora do intervalo
   * representável do `Date`. Validar num sítio só, à saída, é o que garante a
   * invariante de que o `Settings.now` desta app nunca emite um instante que o
   * `Intl` recuse.
   */
  private deriveNowMillis(): number {
    if (this.anchorServerMs === null) {
      return Date.now();
    }

    // Cada relógio lido uma vez só: isto corre a cada `DateTime.now()` da app.
    const wallDelta = Date.now() - this.anchorWallMs;
    const tickDelta = tick() - this.anchorTickMs;
    const fromTick = Math.round(this.anchorServerMs + tickDelta);

    if (wallDelta - tickDelta > DRIFT_TOLERANCE_MS) {
      // O ramo da parede não tem teto: `wallDelta` é a distância a um relógio em
      // que já não se confia, e um salto absurdo empurra a soma para lá do que o
      // `Intl` formata. Aí o contador monotónico, mesmo atrasado, é preferível a
      // um instante impossível.
      const fromWall = Math.round(this.anchorServerMs + wallDelta);
      return isFormattableInstant(fromWall) ? fromWall : fromTick;
    }

    return fromTick;
  }

  /**
   * Fetches the time again, but only when the anchor can no longer be trusted:
   * nothing anchored yet, or the wall clock and the monotonic counter have
   * drifted apart since the anchor was taken.
   *
   * That divergence has two causes and they are indistinguishable from here —
   * the machine was suspended (the counter froze while the world moved on, so
   * the derived time is now behind) or the clock was changed under us. A fetch
   * settles both, and is the only thing that can.
   *
   * Meant for `visibilitychange`: the moments a tab comes back are exactly the
   * moments a suspension may have happened. Throttled to one fetch per minute so
   * a flapping tab cannot turn into a request loop; returns the current state
   * without fetching when there is no reason to.
   */
  public resyncIfDrifted(): Observable<boolean> {
    // O mesmo recuo do {@link resyncOnSchedule} enquanto nada estiver ancorado:
    // sem ele, uma estação com os dois domínios bloqueados dispara dois pedidos
    // falhados a cada ida e volta entre abas, o dia inteiro. O piso plano
    // continua a valer quando há âncora, porque aí isto reage a um sinal real
    // de desvio e não a uma tentativa perdida.
    const required = this.isSynced
      ? MIN_RESYNC_INTERVAL_MS
      : this.retryDelayMs();

    if (tick() - this.lastAttemptTickMs < required) {
      return of(this.isSynced);
    }

    if (this.isSynced && this.wallDriftMs() <= DRIFT_TOLERANCE_MS) {
      return of(true);
    }

    return this.sync();
  }

  /**
   * Tentativa de fundo, chamada a ritmo fixo pelo `SERVER_CLOCK_INITIALIZER`.
   *
   * A política vive aqui, e não no temporizador, para ser testável sem esperar
   * pelo relógio: enquanto nada está ancorado insiste com recuo exponencial
   * ({@link RETRY_BASE_MS} até {@link RETRY_MAX_MS}); já ancorado, revalida a
   * cada {@link PERIODIC_RESYNC_MS}. Fora dessas janelas devolve o estado atual
   * sem gastar um pedido.
   *
   * Complementa o {@link resyncIfDrifted} em vez de o substituir: aquele reage a
   * um evento (a aba a voltar à frente) e este ao tempo, que é o que falta na
   * estação que nunca muda de visibilidade.
   */
  public resyncOnSchedule(): Observable<boolean> {
    const waited = tick() - this.lastAttemptTickMs;
    const required = this.isSynced ? PERIODIC_RESYNC_MS : this.retryDelayMs();

    return waited >= required ? this.sync() : of(this.isSynced);
  }

  /** Whether a source answered. `false` = running on the local clock. */
  public get isSynced(): boolean {
    return this.anchorServerMs !== null;
  }

  /** Machine clock drift in seconds; positive means the machine is behind. */
  public get skewSeconds(): number {
    const machineMs = Date.now();
    if (this.anchorServerMs === null || !isFormattableInstant(machineMs)) {
      return 0;
    }
    return Math.round((this.nowMillis() - machineMs) / 1000);
  }

  /**
   * `false` quando o {@link nowMillis} teve de recorrer à rede de segurança, ou
   * seja quando o instante que ele devolve é inventado.
   *
   * Deliberadamente NÃO inclui o estado do relógio da máquina, que o
   * {@link isMachineClockUsable} reporta à parte. São perguntas diferentes, e
   * juntá-las numa etiqueta só tornava impossível distinguir no Sentry uma
   * estação a emitir datas de 2020 de uma estação ancorada, a emitir datas
   * certas, que apenas tem o RTC estragado.
   */
  public get isClockUsable(): boolean {
    return this.clockUsable;
  }

  /**
   * Se o relógio da PRÓPRIA máquina devolve um instante que o `Intl` aceita.
   *
   * Separado do {@link isClockUsable} de propósito: uma estação ancorada com o
   * relógio partido produz instantes bons a partir da âncora, e sem esta
   * distinção passaria por sã na telemetria — apagando exatamente o sintoma que
   * se quer ver.
   */
  public get isMachineClockUsable(): boolean {
    return isFormattableInstant(Date.now());
  }

  /**
   * Carimba o estado do relógio em todos os eventos seguintes do Sentry.
   *
   * Sem isto, o estado que explica a maioria dos erros de data é invisível: um
   * `RangeError: Invalid time value` chega sem dizer se a estação estava
   * sincronizada, quanto desvio tinha, ou se o relógio dela sequer servia — que
   * foi precisamente o que tornou o THE-NEW-130 indecidível a partir do evento.
   *
   * `setTag` num SDK não inicializado é um no-op, portanto isto é seguro nos
   * shells que não montam o Sentry.
   */
  private reportClockState(): void {
    const machineUsable = this.isMachineClockUsable;

    // O ESTADO, e não o resultado da última tentativa: uma falha de rede numa
    // revalidação periódica não desancora nada — a âncora anterior continua a
    // valer — e etiquetar essa estação como `clock_synced=false` marcá-la-ia
    // como doente para o resto da sessão por causa de um pedido perdido.
    Sentry.setTag('clock_synced', String(this.isSynced));
    Sentry.setTag('clock_machine_usable', String(machineUsable));
    Sentry.setTag('clock_usable', String(this.isClockUsable));

    // O desvio só se mede contra um relógio que se consiga ler. Emitir `0` numa
    // máquina ilegível fá-la-ia passar por perfeitamente acertada, que é o
    // oposto do que esta telemetria existe para mostrar.
    Sentry.setTag(
      'clock_skew_seconds',
      this.isSynced && machineUsable ? String(this.skewSeconds) : 'unknown',
    );
  }

  /**
   * Assinala a estação cujo relógio o `Intl` recusa. Reportado UMA vez: o
   * {@link nowMillis} está no caminho quente de todo o `DateTime.now()` da app.
   */
  private reportUnusableClock(derived: number): void {
    this.clockUsable = false;
    if (this.clockUnusableReported) {
      return;
    }
    this.clockUnusableReported = true;

    Sentry.setTag('clock_usable', 'false');
    Sentry.setContext('clock', {
      derived: String(derived),
      machineNow: String(Date.now()),
      synced: this.isSynced,
    });
  }

  private readSource(source: TTimeSource): Observable<boolean> {
    // Parâmetro descartável, para nunca receber uma hora em cache. A primeira
    // fonte responde `cache-control: private` sem `max-age` e sem validadores,
    // portanto um proxy da rede do cliente pode servir um corpo antigo — e a
    // janela de plausibilidade (2020–2100) aceitá-lo-ia sem hesitar, ancorando
    // a app com toda a confiança numa hora errada.
    //
    // Só um parâmetro, deliberadamente: um header `Cache-Control` tornaria o
    // pedido não-simples e obrigaria a um preflight que a primeira fonte NÃO
    // aceita (o `access-control-allow-headers` dela só cobre `Content-Type`),
    // o que partiria a sincronização em vez de a proteger. E o valor não pode
    // sair do relógio, que é a coisa em que este serviço não confia.
    const params = new HttpParams().set('_', cacheBuster());

    return this.http.get<Record<string, unknown>>(source.url, { params }).pipe(
      timeout(REQUEST_TIMEOUT_MS),
      map((body) => this.anchor(readInstant(body, source))),
      // A dead, blocked or misbehaving source just means "try the next one".
      catchError(() => of(false)),
    );
  }

  private anchor(serverMs: number | null): boolean {
    if (serverMs === null) {
      return false;
    }

    this.anchorServerMs = serverMs;
    this.anchorTickMs = tick();
    this.anchorWallMs = Date.now();
    return true;
  }

  /**
   * Espera exigida antes da próxima tentativa, enquanto nada está ancorado:
   * {@link RETRY_BASE_MS} duplicada a cada falha seguida, com o teto em
   * {@link RETRY_MAX_MS}.
   */
  private retryDelayMs(): number {
    const backoff = RETRY_BASE_MS * 2 ** Math.min(this.consecutiveFailures, 10);
    return Math.min(backoff, RETRY_MAX_MS);
  }

  /**
   * How far the wall clock and the monotonic counter have moved apart since the
   * anchor. Zero on a machine that neither slept nor had its clock touched.
   */
  private wallDriftMs(): number {
    if (this.anchorServerMs === null) {
      return 0;
    }

    const wallDelta = Date.now() - this.anchorWallMs;
    const tickDelta = tick() - this.anchorTickMs;
    return Math.abs(wallDelta - tickDelta);
  }
}

/**
 * Um "agora" de último recurso: obviamente errado, mas que ANDA.
 *
 * Uma constante fixa parece inofensiva e não é. Isto alimenta o `Settings.now`,
 * portanto um valor congelado põe a ZERO todo o tempo decorrido medido em Luxon
 * durante a sessão — o arrefecimento por assinatura do relatório ao Sentry, a
 * janela de tolerância do unload, o `temporaryAuthExpiresAt` do `auth.store`.
 * Somar o contador monotónico mantém a data visivelmente errada (é o que se
 * quer: ver {@link LAST_RESORT_MS}) e a duração correta, que é o que estes
 * consumidores realmente leem.
 *
 * Num ambiente sem `performance.now()` o {@link tick} cai no relógio de parede,
 * que aqui é justamente o que está partido — e então isto congela mesmo, porque
 * não sobra nenhuma fonte monotónica de onde tirar tempo. Congelar é a falha
 * segura: o alternativo seria deixar sair o `NaN`, que é o bug de origem. Todos
 * os browsers suportados têm `performance.now()`, portanto o caso é teórico.
 */
function lastResortMillis(): number {
  const monotonic = tick();
  const elapsed = Number.isFinite(monotonic) ? Math.round(monotonic) : 0;
  const advanced = LAST_RESORT_MS + elapsed;

  return isFormattableInstant(advanced) ? advanced : LAST_RESORT_MS;
}

/** Valor descartável e único por pedido, sem depender do relógio. */
function cacheBuster(): string {
  return Math.random().toString(36).slice(2);
}

/**
 * Monotonic counter, in millis. Falls back to the wall clock where
 * `performance` is missing — the fallback then drifts with a clock change, but
 * it is still better than having no time at all.
 */
function tick(): number {
  return typeof performance?.now === 'function' ? performance.now() : Date.now();
}

/** Pulls the instant out of a source's payload, or `null` if it is unusable. */
function readInstant(
  body: Record<string, unknown> | null | undefined,
  source: TTimeSource,
): number | null {
  if (!body) {
    return null;
  }

  const iso = body[source.isoField];
  if (typeof iso === 'string') {
    // `{ zone: 'utc' }` é obrigatório: as duas fontes são pedidas com
    // `timezone=UTC`, logo uma resposta SEM designador significa "isto é UTC".
    // Sem a opção, o `fromISO` interpretaria essa string em
    // `Settings.defaultZone` — que o `LUXON_ZONE_INITIALIZER` liga ao fuso da
    // conta — e a âncora sairia deslocada pelo offset do tenant. Como o
    // `SERVER_CLOCK_INITIALIZER` liga esta âncora a `Settings.now`, isso
    // deslocaria TODO o `DateTime.now()` da aplicação, e a janela de
    // plausibilidade (2020–2100) é larga demais para apanhar um erro de horas.
    //
    // Uma string que traga `Z` ou offset continua a mandar: a opção só decide
    // como ler as que não trazem nenhum.
    const parsed = DateTime.fromISO(iso, { zone: 'utc' });
    if (parsed.isValid) {
      return plausible(parsed.toMillis());
    }
  }

  const epochSeconds = body[source.epochSecondsField];
  if (typeof epochSeconds === 'number' && Number.isFinite(epochSeconds)) {
    return plausible(epochSeconds * 1000);
  }

  return null;
}

function plausible(ms: number): number | null {
  return ms >= PLAUSIBLE_FROM_MS && ms < PLAUSIBLE_TO_MS ? ms : null;
}
